// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@conklin/App/Components/Notify";
import { productFeedback } from "@conklin/App/Services/ProducFeedback";
import TextArea, { TextAreaProps } from "@conklin/MobiusOverrides/TextArea/TextArea";
import { mergeWithCss, xValidateOnSubmit } from "@conklin/XDL/Utilities";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";

interface LeaveUsFeedbackProps {
    itemCode?: string;
    sku?: string;
    extendedStyles?: LeaveUsFeedbackStyles;
}

type Props = LeaveUsFeedbackProps;

export interface LeaveUsFeedbackStyles {
    leaveUsFeedback?: LinkPresentationProps;
    modal?: ModalPresentationProps;
    submitFeedbackButton?: ButtonPresentationProps;
    yourMessageTextArea?: TextAreaProps;
    buttonWrapper?: InjectableCss;
    errorNotify?: TypographyProps;
}
export const leaveUsFeedbackStyles: LeaveUsFeedbackStyles = {};

const formId = "LeaveFeedbackForm";

const LeaveFeedback: FC<Props> = ({ itemCode, sku, extendedStyles }: Props) => {
    const [styles] = React.useState(() => mergeWithCss(leaveUsFeedbackStyles, extendedStyles));
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [yourMessage, setYourMessage] = React.useState("");
    const [yourMessageError, setYourMessageError] = React.useState("");
    const [submitError, setSubmitError] = React.useState("");
    const [inProgress, setInProgress] = React.useState(false);
    const toasterContext = useContext(ToasterContext);

    const requiredFieldMessage = translate("Your Message is a required field");

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const feedbackLinkClickHandler = () => {
        resetFields();
        setModalIsOpen(true);
    };

    const resetFields = () => {
        setYourMessage("");
        setYourMessageError("");
        setInProgress(false);
        setSubmitError("");
    };

    const yourMessageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setYourMessage(event.target.value);
        setYourMessageError(!event.target.value ? requiredFieldMessage : "");
    };

    const submitFeedbackClickHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const errors = xValidateOnSubmit(formId);

        if (!yourMessage) {
            setYourMessageError(requiredFieldMessage);
            return;
        }

        if (errors && errors.length > 0) {
            return;
        }

        setInProgress(true);

        const result = await productFeedback({
            Sku: sku || "",
            ItemCode: itemCode || "",
            FeedbackMessage: yourMessage,
        });
        setInProgress(false);

        if (result.successful) {
            setModalIsOpen(false);
            toasterContext.addToast({
                body: siteMessage("Custom_ProductFeedbackSuccess_Message"),
                messageType: "success",
            });
        } else {
            setSubmitError(`${siteMessage("Custom_ProductFeedbackFailed_Message")}`);
        }
    };
    const StyledForm = getStyledWrapper("form");

    return (
        <>
            <Link
                aria-label={translate("Help Us Improve Product With Feedback")}
                {...styles.leaveUsFeedback}
                onClick={feedbackLinkClickHandler}
            >
                {translate("Help Us Improve Product With Feedback")}
            </Link>

            <Modal
                {...styles.modal}
                headline={translate("Leave Us Feedback")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
                sizeVariant="small"
            >
                <StyledForm onSubmit={submitFeedbackClickHandler} noValidate>
                    <TextArea
                        label={translate("Your Message")}
                        value={yourMessage}
                        error={yourMessageError}
                        onChange={yourMessageChangeHandler}
                        data-test-selector="productShareYourMessage"
                        required
                        {...styles.yourMessageTextArea}
                        {...{ formId }}
                    />
                    {submitError && (
                        <Notify type="danger" showClose={false} {...styles.errorNotify}>
                            {submitError}
                        </Notify>
                    )}
                    <StyledWrapper {...styles.buttonWrapper}>
                        <Button type="submit" {...styles.submitFeedbackButton} disabled={inProgress}>
                            {translate("Submit")}
                        </Button>
                    </StyledWrapper>
                </StyledForm>
            </Modal>
        </>
    );
};

export default LeaveFeedback;
